import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Consultations = () => {
  const Navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [search, setSearch] = useState("");
  const [formFilter, setFormFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // ✅ Detect screen size dynamically
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768); // <768px => mobile
    };

    handleResize(); // check on mount
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchConsultations = (pageNumber = 1) => {
    setIsLoading(true);
    const params = new URLSearchParams();
    params.append("page", pageNumber);
    const limit = 10;
    params.append("limit", limit);

    if (search) params.append("search", search);
    if (formFilter && formFilter.toLowerCase() !== "all") {
      params.append("consultation_for", formFilter.toLowerCase());
    }

    axios
      .get(`${process.env.REACT_APP_URL}/admin/form?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      })
      .then((res) => {
        const responseData = res?.data?.data || res?.data;
        const forms =
          responseData?.forms || responseData?.rows || responseData?.list || [];
        setTotalPage(responseData?.pagination?.totalPages || 1);
        setData(forms);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Failed to load consultations",
        );
        if (err.response?.data?.message === "Unauthorized!") {
          localStorage.removeItem("Token");
          Navigate("/admin/login");
        }
      })
      .finally(() => setIsLoading(false));
  };

  const exportToExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (formFilter && formFilter.toLowerCase() !== "all") {
        params.append("consultation_for", formFilter.toLowerCase());
      }
      params.append("page", 1);
      params.append("limit", 10000);

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/admin/form?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        },
      );

      const exportData =
        response?.data?.data?.forms ||
        response?.data?.data?.rows ||
        response?.data?.data?.list ||
        [];

      if (exportData.length === 0) {
        toast("No data to export.", { icon: "⚠️" });
        return;
      }

      // ✅ Format all form fields properly
      const formatted = exportData.map((form, i) => ({
        "Sr. No.": i + 1,
        "Form ID": form.form_id,
        "Consultation For": form.consultation_for,
        Name: form.name,
        Email: form.email,
        Phone: form.phone,
        Company: form.company,
        Category: form.category,
        "Packaging Type": form.packaging_type,
        "Product Name": form.product_name,
        "Target Quantity": form.target_quantity,
        Budget: form.budget,
        "Estimated Pallets": form.estimated_pallets,
        Timeline: form.timeline,
        "Preferred Location": form.prefered_location,
        "Space Requirements": form.space_requirements,
        "Brief Notes": form.brief_notes,
        Message: form.message,
        "Terms Accepted": form.terms ? "Yes" : "No",
        "Is Active": form.is_active ? "Yes" : "No",
        "Is Deleted": form.is_deleted ? "Yes" : "No",
        "Created At": new Date(form.created_at).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      }));

      // === Create Sheet ===
      const header = Object.keys(formatted[0]);
      const ws = XLSX.utils.json_to_sheet(formatted, {
        skipHeader: true,
        origin: "A2",
      });
      XLSX.utils.sheet_add_aoa(ws, [header], { origin: "A1" });

      // Set column widths
      ws["!cols"] = header.map((h) => ({ wch: Math.max(h.length + 2, 18) }));

      // Header Styling
      header.forEach((col, i) => {
        const cell = ws[XLSX.utils.encode_cell({ r: 0, c: i })];
        if (cell)
          cell.s = {
            fill: { fgColor: { rgb: "ADD8E6" } }, // light blue
            font: { bold: true, color: { rgb: "000000" } },
            alignment: { horizontal: "center", vertical: "center" },
          };
      });

      // Alternate Row Shading
      formatted.forEach((_, r) => {
        if (r % 2 === 1)
          header.forEach((_, c) => {
            const cell = ws[XLSX.utils.encode_cell({ r: r + 1, c })];
            if (cell)
              cell.s = {
                fill: { fgColor: { rgb: "F9F9F9" } },
                alignment: { horizontal: "left", vertical: "center" },
              };
          });
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Consultations");

      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });

      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(fileData, "consultations-list.xlsx");
      toast.success("Export successful!");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Failed to export data.");
    }
  };

  useEffect(() => {
    fetchConsultations(page);
  }, [page]);

  useEffect(() => {
    setPage(1);
    fetchConsultations(1);
  }, [search, formFilter]);

  const formatText = (val) =>
    val && val !== "null" && val !== "undefined" ? val : "—";

  const BADGE = {
    procurement: { bg: "var(--color-success-bg)", color: "var(--color-success-text)" },
    warehouse: { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
    design_team: { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
    contact: { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
    consultation: { bg: "var(--color-primary-weak)", color: "var(--color-primary)" },
    news_letter: { bg: "var(--color-surface-2)", color: "var(--color-text-secondary)" },
  };
  const statusBadge = (val) => {
    const t = BADGE[val] || { bg: "var(--color-surface-2)", color: "var(--color-text-secondary)" };
    return (
      <span className="al-pill" style={{ background: t.bg, color: t.color, textTransform: "capitalize" }}>
        {val?.replace("_", " ") || "—"}
      </span>
    );
  };

  const handlePageClick = (e) => setPage(e.selected + 1);

  const [dense, setDense] = useState(false);
  const FILTERS = ["All", "Contact", "Consultation", "Warehouse", "Design Team", "Procurement", "News Letter"];
  const GRID = { gridTemplateColumns: "56px 150px 1.2fr 1.5fr 120px 1fr 1fr 1fr 1fr 1.4fr 110px 110px 120px" };

  return (
    <div className="al">
      <Toaster position="top-center" />

      <div className="al-head">
        <div>
          <h1 className="al-title">Consultations &amp; Form Submissions</h1>
          <p className="al-sub">Enquiries and form submissions from across the marketplace.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn al-btn--primary" onClick={exportToExcel}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export Excel
          </button>
        </div>
      </div>

      <div className="al-tabs">
        {FILTERS.map((f) => (
          <button key={f} type="button"
            className={`al-tab ${formFilter.toLowerCase() === f.toLowerCase() ? "is-active" : ""}`}
            onClick={() => setFormFilter(f.toLowerCase())}>
            {f}
          </button>
        ))}
      </div>

      <div className="al-card">
        <div className="al-toolbar">
          <div className="al-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            <input type="text" placeholder="Search submissions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchConsultations(1)} />
          </div>
          <button type="button" className="al-tool" style={{ marginLeft: "auto" }} onClick={() => setDense((d) => !d)} title="Toggle density">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        {!isMobile ? (
          <div className="al-tablewrap">
            <div style={{ minWidth: 1600, "--al-row-pad": dense ? "8px" : "13px" }}>
              <div className="al-thead" style={GRID}>
                <span className="al-th">#</span>
                <span className="al-th">For</span>
                <span className="al-th">Name</span>
                <span className="al-th">Email</span>
                <span className="al-th">Phone</span>
                <span className="al-th">Company</span>
                <span className="al-th">Category</span>
                <span className="al-th">Packaging</span>
                <span className="al-th">Timeline</span>
                <span className="al-th">Brief Notes</span>
                <span className="al-th">Target Qty</span>
                <span className="al-th">Budget</span>
                <span className="al-th">Est. Pallets</span>
              </div>

              {isLoading ? (
                [...Array(8)].map((_, i) => (
                  <div className="al-row" style={GRID} key={i}>
                    {[...Array(13)].map((__, j) => (
                      <span key={j} className="al-skel" style={{ width: j === 0 ? 24 : "70%" }} />
                    ))}
                  </div>
                ))
              ) : data.length > 0 ? (
                data.map((item, index) => (
                  <div className="al-row" style={GRID} key={item.id ?? index}>
                    <span className="al-muted">{(page - 1) * 10 + index + 1}</span>
                    <span>{statusBadge(item.consultation_for)}</span>
                    <span className="al-name">{formatText(item.name)}</span>
                    <span className="al-muted al-ellipsis" title={item.email}>{formatText(item.email)}</span>
                    <span className="al-mono al-muted">{formatText(item.phone)}</span>
                    <span className="al-muted al-ellipsis" title={item.company}>{formatText(item.company)}</span>
                    <span className="al-muted al-ellipsis" title={item.category}>{formatText(item.category)}</span>
                    <span className="al-muted al-ellipsis" title={item.packaging_type}>{formatText(item.packaging_type)}</span>
                    <span className="al-muted al-ellipsis" title={item.timeline}>{formatText(item.timeline)}</span>
                    <span className="al-muted al-ellipsis" title={item.brief_notes}>{formatText(item.brief_notes)}</span>
                    <span className="al-muted">{formatText(item.target_quantity)}</span>
                    <span className="al-muted">{formatText(item.budget)}</span>
                    <span className="al-muted">{formatText(item.estimated_pallets)}</span>
                  </div>
                ))
              ) : (
                <div className="al-empty">
                  <div className="al-empty__icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  </div>
                  <p className="al-empty__title">No Records Found</p>
                  <p className="al-empty__text">Try a different filter or search term.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="al-mobile">
            {isLoading ? (
              <div className="al-empty"><p className="al-empty__title">Loading…</p></div>
            ) : data.length > 0 ? (
              data.map((item, index) => (
                <div className="al-mcard" key={item.id ?? index}>
                  <div className="al-mcard__top">
                    <div className="al-name" style={{ flex: 1 }}>
                      #{(page - 1) * 10 + index + 1} — {formatText(item.name)}
                    </div>
                    {statusBadge(item.consultation_for)}
                  </div>
                  <div className="al-mrow"><span className="al-muted">Email</span><span>{formatText(item.email)}</span></div>
                  <div className="al-mrow"><span className="al-muted">Phone</span><span className="al-mono">{formatText(item.phone)}</span></div>
                  <div className="al-mrow"><span className="al-muted">Company</span><span>{formatText(item.company)}</span></div>
                  <div className="al-mrow"><span className="al-muted">Category</span><span>{formatText(item.category)}</span></div>
                  <div className="al-mrow"><span className="al-muted">Packaging</span><span>{formatText(item.packaging_type)}</span></div>
                  <div className="al-mrow"><span className="al-muted">Timeline</span><span>{formatText(item.timeline)}</span></div>
                  <div className="al-mrow"><span className="al-muted">Target Qty</span><span>{formatText(item.target_quantity)}</span></div>
                  <div className="al-mrow"><span className="al-muted">Budget</span><span>{formatText(item.budget)}</span></div>
                  <div className="al-mrow"><span className="al-muted">Est. Pallets</span><span>{formatText(item.estimated_pallets)}</span></div>
                  {item.brief_notes && (
                    <div className="al-mrow" style={{ display: "block" }}>
                      <span className="al-muted">Brief Notes</span>
                      <div style={{ marginTop: 4 }}>{item.brief_notes}</div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="al-empty"><p className="al-empty__title">No Records Found</p></div>
            )}
          </div>
        )}

        <div className="al-foot">
          <span className="al-foot__info">Showing <strong>{data?.length || 0}</strong> submissions</span>
          {data && data.length !== 0 && (
            <ReactPaginate
              previousLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>}
              nextLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
              breakLabel={"..."}
              pageCount={totalPage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              forcePage={page - 1}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"} pageLinkClassName={"page-link"}
              previousClassName={"page-item"} previousLinkClassName={"page-link"}
              nextClassName={"page-item"} nextLinkClassName={"page-link"}
              breakClassName={"page-item"} breakLinkClassName={"page-link"}
              activeClassName={"active"} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Consultations;
